/* eslint frontbucket-patterns/no-new-sagas: "warn" */
import { all, takeEvery, takeLatest } from 'redux-saga/effects';

import { UPDATE_GLOBAL_IS_SINGLE_FILE_MODE_ENABLED_SUCCESS } from 'src/redux/diff-settings/index';
import { initCreateJiraIssueSaga } from 'src/redux/jira/sagas/create-jira-issue-saga';
import { fetchPullRequestJiraIssuesSaga } from 'src/redux/jira/sagas/pull-request-jira-issues-saga';
import {
  EXPAND_CONTEXT,
  SCROLL_TO_FILE,
  ENTERED_CODE_REVIEW,
  FETCH_MERGE_CHECKS_RETRY,
  ADD_COMMENT,
  REQUEST_CHANGES,
  UNDO_REQUEST_CHANGES,
  EXITED_CODE_REVIEW,
  REFRESH_CODE_REVIEW_DATA,
  REFRESH_CODE_REVIEW_DATA_FROM_POLL_RESULTS,
  LOAD_PULL_REQUEST,
  INITIAL_DIFFS_RENDERED,
  TOGGLE_DIFF_EXPANSION,
  TASK_STATE_CHANGE,
  TASK_CREATE,
  TASK_DELETE,
  RETRY_LOAD_DIFF_STAT,
  REVERT_PULL_REQUEST,
  TASK_EDIT,
  TOGGLE_COMMENT_LIKE,
  PULL_REQUEST_OPENED,
  PUBLISH_PULL_REQUEST_UI_EVENT,
  PUBLISH_PULL_REQUEST_TRACK_EVENT,
  PUBLISH_PIPELINES_PULL_REQUEST_SCREEN_EVENT,
  CHANGE_DIFF_SORT,
  UPDATE_FILTER_COMMENTS_BY_USER,
  UPDATE_TITLE,
  UPDATE_DESCRIPTION,
  REMOVE_REVIEWER,
  ADD_REVIEWER,
  VERIFY_REPO_MEMBER,
  APPROVAL_SUCCESS,
  PULL_REQUEST_TAB_REDIRECT,
  UPDATE_PULL_REQUEST_QUEUED_STATUS,
} from 'src/redux/pull-request/actions';
import {
  CREATE_PENDING_MERGE,
  CANCEL_PENDING_MERGE,
  FETCH_PENDING_MERGE_STATUS,
  OPEN_MERGE_DIALOG,
  MERGE,
} from 'src/redux/pull-request/merge-reducer';
import { VIEW_ENTIRE_FILE } from 'src/redux/pull-request/view-entire-file-reducer';
import { prCommitActions } from 'src/redux/repo-commits/actions';
import { rootPrCommitsSaga } from 'src/redux/repo-commits/sagas';
import { MergeCheckKey } from 'src/types';

import { FETCH_ACTIVITY } from '../activity-reducer';
import * as api from '../api';
import { UPLOAD_IMAGE } from '../image-upload-reducer';

import {
  publishScreenEventSaga,
  publishUiEventSaga,
  publishTrackEventSaga,
  publishPipelinesScreenEventSaga,
} from './analytics-sagas';
import { cancelPendingMergeSaga } from './cancel-pending-merge-saga';
import { codeReviewDataSaga } from './code-review-data-saga';
import { commentsContextSaga } from './comments-context-saga';
import { createPendingMergeSaga } from './create-pending-merge-saga';
import { createTaskSaga } from './create-task-saga';
import { declinePullRequestSaga } from './decline-pull-request-saga';
import { watchDeleteTask as deleteTaskSaga } from './delete-task-saga';
import { editTaskSaga, editTasksSaga } from './edit-task-saga';
import { expandContextSaga } from './expand-context-saga';
import { fetchActivityFeed } from './fetch-activity-saga';
import { fetchCodeInsightsReportsSaga as codeInsightsSaga } from './fetch-code-insights-reports-saga';
import {
  fetchOutdatedCommentContext,
  fetchCommentContext,
  fetchPendingCommentContext,
  fetchBaseRevCommentContext,
} from './fetch-comment-context-saga';
import { fetchCommentLikes } from './fetch-comment-likes-saga';
import { fetchCommitsStatusesSaga } from './fetch-commit-statuses';
import { viewEntireDiffFileSaga } from './fetch-diff-saga';
import { fetchDiffStatForCurrentPullRequest } from './fetch-diffstat-saga';
import {
  retryFetchMergeChecks,
  retryFetchConcreteMergeChecks,
} from './fetch-merge-checks-saga';
import { fetchPendingMergeStatusSaga } from './fetch-pending-merge-status-saga';
import { fetchPullRequestAnnotations } from './fetch-pull-request-annotations-saga';
import { fetchPullRequestComments } from './fetch-pull-request-comments-saga';
import { fetchSourceBranchDetailsSaga } from './fetch-source-branch-details-saga';
import { fetchStackedPullRequestsCountSaga } from './fetch-stacked-pull-requests-count-saga';
import { globalSingleFileModeEnabledSaga } from './file-mode-onboarding-saga';
import { rememberImageUploadCallbacks } from './image-upload-callback-saga';
import { imageUploaderSaga } from './image-upload-saga';
import { viewPullRequestCapabilitySaga } from './measure-sli-saga';
import {
  mergePullRequestSaga,
  pollMergeTaskStatusSaga,
  showPullRequestMergedFlag,
} from './merge-pull-request-saga';
import {
  startingPollingForUpdates,
  dismissUpdateFlag,
  minimalUpdateFromPollResults,
  minimalUpdateSaga,
} from './poll-pullrequest-updates-saga';
import { pollQueueTaskStatusSaga } from './queue-pull-request-saga';
import { reprocessDiffSaga } from './reprocess-diff-saga';
import { requestPullRequestChanges } from './request-changes-saga';
import { revertPullRequestSaga } from './revert-pull-request-saga';
import {
  scrollToLine,
  scrollToFilepath,
  scrollToGenericPermalink,
  permalinkChangedScrollerSaga,
  permalinkClickedScrollerSaga,
  fileScrollerSaga,
  activeFileCollapsedScrollerSaga,
  scrollToHiddenFile,
  scrollToFilteredSingleFile,
  pullRequestCommentPermalinkScrollerSaga,
  commitCommentPermalinkScrollerSaga,
} from './scrolling-sagas';
import { startWatchSaga } from './start-watch-saga';
import { stopWatchSaga } from './stop-watch-saga';
import { handleTaskStateChangeSaga as taskStateChangeSaga } from './task-state-change-saga';
import { toggleCommentLike } from './toggle-comment-like-saga';
import { undoPullRequestChangesRequest } from './undo-changes-request-saga';
import { updateLastSeenSaga } from './update-last-seen-saga';
import {
  updatePrTitleSaga,
  updatePrDescriptionSaga,
  removeReviewerSaga,
  addReviewerSaga,
} from './update-pull-request-field-saga';
import { verifyRepoMemberSaga } from './verify-repo-member-saga';
import { watchSaga } from './watch-saga';

export function* pullRequestSagas() {
  yield all([
    rootPrCommitsSaga(),
    mergePullRequestSaga(),
    takeLatest(
      UPDATE_PULL_REQUEST_QUEUED_STATUS.SUCCESS,
      pollQueueTaskStatusSaga
    ),
    takeLatest(LOAD_PULL_REQUEST.SUCCESS, pollQueueTaskStatusSaga),
    takeLatest(LOAD_PULL_REQUEST.SUCCESS, pollMergeTaskStatusSaga),
    takeLatest(LOAD_PULL_REQUEST.SUCCESS, fetchPullRequestComments),
    takeLatest(LOAD_PULL_REQUEST.SUCCESS, fetchCommentLikes),
    takeLatest(LOAD_PULL_REQUEST.SUCCESS, fetchSourceBranchDetailsSaga),
    takeLatest(FETCH_PENDING_MERGE_STATUS.REQUEST, fetchPendingMergeStatusSaga),
    takeLatest(CREATE_PENDING_MERGE.REQUEST, createPendingMergeSaga),
    takeLatest(CANCEL_PENDING_MERGE.REQUEST, cancelPendingMergeSaga),
    takeEvery(UPDATE_TITLE.REQUEST, updatePrTitleSaga),
    takeEvery(UPDATE_DESCRIPTION.REQUEST, updatePrDescriptionSaga),
    takeEvery(REMOVE_REVIEWER.REQUEST, removeReviewerSaga),
    takeEvery(ADD_REVIEWER.REQUEST, addReviewerSaga),
    takeEvery(REQUEST_CHANGES.REQUEST, requestPullRequestChanges),
    takeEvery(UNDO_REQUEST_CHANGES.REQUEST, undoPullRequestChangesRequest),
    declinePullRequestSaga(),
    takeEvery(UPLOAD_IMAGE.REQUEST, imageUploaderSaga),
    rememberImageUploadCallbacks(),
    taskStateChangeSaga(),
    createTaskSaga(),
    takeEvery([TASK_EDIT.REQUEST], editTaskSaga),
    takeEvery([TOGGLE_COMMENT_LIKE.REQUEST], toggleCommentLike),
    editTasksSaga(),
    deleteTaskSaga(),
    takeEvery(EXPAND_CONTEXT.REQUEST, expandContextSaga),
    takeLatest(ENTERED_CODE_REVIEW, scrollToGenericPermalink),
    takeLatest(INITIAL_DIFFS_RENDERED, scrollToLine),
    takeLatest(INITIAL_DIFFS_RENDERED, scrollToFilepath),
    takeLatest(
      [ENTERED_CODE_REVIEW, PULL_REQUEST_TAB_REDIRECT],
      pullRequestCommentPermalinkScrollerSaga
    ),
    commitCommentPermalinkScrollerSaga(),
    permalinkClickedScrollerSaga(),
    permalinkChangedScrollerSaga(),
    scrollToHiddenFile(),
    takeLatest(CHANGE_DIFF_SORT, scrollToFilteredSingleFile),
    takeLatest(UPDATE_FILTER_COMMENTS_BY_USER, scrollToFilteredSingleFile),
    takeLatest(SCROLL_TO_FILE, fileScrollerSaga),
    takeEvery(PULL_REQUEST_OPENED, publishScreenEventSaga),
    takeEvery(PUBLISH_PULL_REQUEST_TRACK_EVENT, publishTrackEventSaga),
    takeEvery(
      PUBLISH_PIPELINES_PULL_REQUEST_SCREEN_EVENT,
      publishPipelinesScreenEventSaga
    ),
    takeEvery(PUBLISH_PULL_REQUEST_UI_EVENT, publishUiEventSaga),
    commentsContextSaga(),
    takeLatest(ENTERED_CODE_REVIEW, viewPullRequestCapabilitySaga),
    takeLatest(ENTERED_CODE_REVIEW, codeReviewDataSaga),
    takeLatest(ENTERED_CODE_REVIEW, startingPollingForUpdates),
    takeLatest(ENTERED_CODE_REVIEW, initCreateJiraIssueSaga),
    takeLatest(ENTERED_CODE_REVIEW, fetchPullRequestJiraIssuesSaga),
    takeLatest(
      UPDATE_GLOBAL_IS_SINGLE_FILE_MODE_ENABLED_SUCCESS,
      globalSingleFileModeEnabledSaga
    ),
    takeLatest(EXITED_CODE_REVIEW, dismissUpdateFlag),
    takeLatest(FETCH_MERGE_CHECKS_RETRY, retryFetchMergeChecks),
    takeLatest(
      [TASK_STATE_CHANGE.SUCCESS, TASK_CREATE.SUCCESS, TASK_DELETE.SUCCESS],
      retryFetchConcreteMergeChecks,
      // @ts-ignore TS defs for redux-saga aren't perfect
      [MergeCheckKey.RESOLVED_TASKS]
    ),
    takeLatest(
      [APPROVAL_SUCCESS, REQUEST_CHANGES.SUCCESS, UNDO_REQUEST_CHANGES.SUCCESS],
      retryFetchConcreteMergeChecks,
      // @ts-ignore TS defs for redux-saga aren't perfect
      [
        MergeCheckKey.MINIMUM_APPROVALS,
        MergeCheckKey.MINIMUM_DEFAULT_REVIEWER_APPROVALS,
        MergeCheckKey.NO_CHANGES_REQUESTED,
        MergeCheckKey.REVIEW_GROUPS,
      ] as MergeCheckKey[]
    ),
    takeLatest(REFRESH_CODE_REVIEW_DATA, minimalUpdateSaga),
    takeLatest(
      REFRESH_CODE_REVIEW_DATA_FROM_POLL_RESULTS,
      minimalUpdateFromPollResults
    ),
    takeLatest(FETCH_ACTIVITY.REQUEST, fetchActivityFeed),
    reprocessDiffSaga(),
    takeLatest(
      // api.getWatch will also get called when user enters code review (ENTERED_CODE_REVIEW)
      // but that has to be done from the code review data saga since PR store might
      // not be ready yet in certain scenarios.
      [ADD_COMMENT.SUCCESS, APPROVAL_SUCCESS, REQUEST_CHANGES.SUCCESS],
      watchSaga,
      // @ts-ignore TS defs for redux-saga aren't perfect
      api.getWatch
    ),
    takeLatest(
      // @ts-ignore TODO: fix noImplicitAny error here
      action =>
        action.type === TOGGLE_DIFF_EXPANSION &&
        action.payload.isOpening === false,
      activeFileCollapsedScrollerSaga
    ),
    startWatchSaga(),
    stopWatchSaga(),
    fetchOutdatedCommentContext(api.getCommentById),
    fetchCommentContext(api.getCommentById),
    fetchPendingCommentContext(api.getCommentById),
    fetchBaseRevCommentContext(api.getCommentById),
    takeLatest(
      [ADD_COMMENT.SUCCESS, LOAD_PULL_REQUEST.SUCCESS],
      updateLastSeenSaga
    ),
    takeLatest(OPEN_MERGE_DIALOG, fetchStackedPullRequestsCountSaga),
    takeLatest(RETRY_LOAD_DIFF_STAT, fetchDiffStatForCurrentPullRequest),
    takeLatest(REVERT_PULL_REQUEST.REQUEST, revertPullRequestSaga),
    takeLatest(prCommitActions.UPDATE_COMMITS, fetchCommitsStatusesSaga),
    codeInsightsSaga(),
    takeLatest(LOAD_PULL_REQUEST.SUCCESS, fetchPullRequestAnnotations),
    takeLatest(VIEW_ENTIRE_FILE, viewEntireDiffFileSaga),
    takeEvery(VERIFY_REPO_MEMBER, verifyRepoMemberSaga),
    takeLatest(MERGE.SUCCESS, showPullRequestMergedFlag),
  ]);
}
